/**
 * BusquedaGlobal — buscador cross-section que encuentra cualquier reserva
 * sin importar la sub-sección donde esté (Hoy, Próximas, Hold, Histórico).
 *
 * Atajo: Cmd/Ctrl+K para enfocar. ESC para cerrar.
 * Click en resultado → abre SlideOver de detalle.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, TextField, InputAdornment, Paper, Typography, Chip, IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EstadoChip from './EstadoChip';
import SaldoDestinoChip from './SaldoDestinoChip';
import VoucherSubsanarChip from './VoucherSubsanarChip';
import { API_BASE, getAuthHeaders, getEmpresaId, norm, formatFechaCorta, fmtPax, formatPrecio } from './format';
import { formatNombre } from '../../../utils/formatNombre';

export default function BusquedaGlobal({ reservaciones, onOpen }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Búsqueda SERVER-SIDE con debounce — evita el bug del subset local que
  // pierde reservas creadas con anticipación (fuera del top 500 por
  // created_at DESC). Cuando el operador escribe ≥2 chars, dispara un fetch
  // al backend con `q=` que busca en TODA la BD (código, cliente, whatsapp,
  // correo, documento).
  const [resultadosServer, setResultadosServer] = useState([]);
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    const qn = q.trim();
    if (qn.length < 2) {
      setResultadosServer([]);
      setBuscando(false);
      return undefined;
    }
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${API_BASE}/turismo/reservaciones?empresa_id=${getEmpresaId()}`
          + `&q=${encodeURIComponent(qn)}&limit=25`,
          { headers: getAuthHeaders() }
        );
        if (!r.ok) throw new Error(String(r.status));
        const data = await r.json();
        setResultadosServer(Array.isArray(data?.reservaciones) ? data.reservaciones : []);
      } catch (e) {
        setResultadosServer([]);
      } finally {
        setBuscando(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Atajo Cmd/Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click fuera cierra
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const resultados = useMemo(() => {
    const qn = norm(q.trim());
    if (qn.length < 2) return [];
    // Fuente principal: resultados server-side (encuentran cualquier reserva
    // en la BD sin importar cuándo se crearon). Como respaldo, si el fetch
    // aún no responde o falla, hacemos también el filtro local sobre el
    // subset ya cargado para que el operador vea algo mientras carga.
    const local = reservaciones.filter(r => {
      const blob = norm([
        r.codigo_reserva, r.nombre_cliente, r.numero_whatsapp, r.correo,
        r.agencia_nombre, r.paquete_nombre, r.alquiler_nombre,
      ].join(' '));
      return blob.includes(qn);
    });
    // Merge por id (server manda): server first, luego locales que no estén.
    const ids = new Set(resultadosServer.map(r => r.id));
    const extras = local.filter(r => !ids.has(r.id));
    return [...resultadosServer, ...extras].slice(0, 15);
  }, [reservaciones, resultadosServer, q]);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', mb: 1.5 }}>
      <TextField
        inputRef={inputRef}
        fullWidth size="small"
        placeholder='Buscar cualquier reserva (código, cliente, teléfono, agencia, paquete)…'
        value={q}
        onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: '#0d9488' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {q && (
                <IconButton size="small" onClick={() => { setQ(''); inputRef.current?.focus(); }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              <Chip size="small" label="⌘K"
                sx={{ fontSize: 9, height: 18, ml: 0.5, bgcolor: '#f1f5f9', fontFamily: 'monospace' }} />
            </InputAdornment>
          ),
          sx: { bgcolor: '#fff', borderRadius: 2, fontSize: 13 },
        }}
      />

      {open && q.length >= 2 && (
        <Paper sx={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300,
          mt: 0.5, maxHeight: 460, overflowY: 'auto',
          borderRadius: 2, boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
          border: '1px solid #e2e8f0',
        }}>
          <Box sx={{ p: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              {resultados.length === 0 ? 'Sin resultados' : `${resultados.length} resultado(s)`}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
              Busca en TODAS las sub-secciones (Hoy, Próximas, Hold, Histórico)
            </Typography>
          </Box>

          {resultados.length === 0 && q.length >= 2 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                Ninguna reserva coincide con <b>"{q}"</b>
              </Typography>
            </Box>
          )}

          {resultados.map(r => (
            <Box key={r.id}
              onClick={() => { onOpen?.(r.id); setOpen(false); setQ(''); }}
              sx={{
                p: 1.25, cursor: 'pointer',
                borderBottom: '1px solid #f8fafc',
                '&:hover': { bgcolor: '#f0fdfa' },
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
              }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
                           mb: 0.25, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                    {r.codigo_reserva}
                  </Typography>
                  <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
                  <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                    saldoACargoDe={r.saldo_a_cargo_de}
                                    saldoMuelleMonto={r.saldo_muelle_monto}
                                    cxcAgenciaMonto={r.cxc_agencia_monto}
                                    formaPagoAgencia={r.forma_pago_agencia} />
                  <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }} noWrap>
                  {formatNombre(r.nombre_cliente) || '—'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }} noWrap>
                  {r.paquete_nombre || r.alquiler_nombre || '—'}
                  {r.agencia_nombre && <> · 🏢 {r.agencia_nombre}</>}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 110 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488' }}>
                  {formatFechaCorta(r.salida_fecha)}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  {fmtPax(r)} · {formatPrecio(r.precio_total, r.moneda)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}
